import type { ClinicData, ClinicSettings, TemplateKey, TreatmentOption } from '@/lib/clinic/types'

export const STORAGE_KEY = 'dr-sayuja-clinic/v1'

export const DEFAULT_TEMPLATES: Record<TemplateKey, string> = {
    reminder: "Hello [Patient Name], this is a friendly reminder from Dr. Sayuja Clinic. Your next dental visit is scheduled for [Date]. Please contact us if you need to reschedule. Thank you.",
    overdue: "Hello [Patient Name], we hope you are doing well. Our records indicate that your dental follow-up is due. Please contact Dr. Sayuja Clinic to schedule your next visit.",
    confirmation: "Hello [Patient Name], your appointment at Dr. Sayuja Clinic is confirmed for [Date] at [Time]. Thank you."
}

export const TEMPLATE_PLACEHOLDERS = [
    '[Patient Name]',
    '[Date]',
    '[Time]',
    '[Treatment]',
    '[Clinic Name]',
    '[Clinic Phone]',
    '[Doctor Name]'
] as const

export const DEFAULT_TREATMENT_NAMES = [
    'Teeth Cleaning / Scaling',
    'Root Canal Treatment',
    'Tooth Extraction',
    'Dental Filling',
    'Dental Crown',
    'Dental Bridge',
    'Teeth Whitening',
    'Denture',
    'Gum Treatment',
    'Dental Checkup',
    'Tooth Pain',
    'Other'
]

export const createDefaultTreatments = (): TreatmentOption[] => {
    return DEFAULT_TREATMENT_NAMES.map((name, index) => ({ id: `tr_${index + 1}`, name }))
}

export const createDefaultSettings = (): ClinicSettings => ({
    clinic: {
        name: 'Dr. Sayuja Clinic',
        tagline: 'Dental Care & Oral Health',
        logo: null,
        doctorName: 'Dr. Sayuja',
        qualification: 'BDS, MDS (Conservative Dentistry & Endodontics)',
        registrationNo: 'Reg. No. KDC/2016/0418',
        address: '2nd Floor, Lakeside Arcade, Seaport-Airport Road, Kakkanad, Kochi, Kerala 682030',
        phone: '+91 94470 58210',
        email: '',
        hours: 'Mon to Sat, 9:30 AM to 7:30 PM'
    },
    prescription: {
        defaultInstructions: [
            'Take the medicines after food unless told otherwise, and complete the full course.',
            'Rinse gently with warm salt water after meals.',
            'Call the clinic if you notice severe pain, swelling or bleeding.'
        ].join('\n'),
        footer: 'Please bring this prescription on your next visit. Clinic hours: Mon to Sat, 9:30 AM to 7:30 PM.',
        signatureName: 'Dr. Sayuja',
        signatureTitle: 'BDS, MDS',
        layout: 'full',
        showLogo: true,
        showAllergies: true,
        showNextVisit: true
    },
    followUp: {
        templates: { ...DEFAULT_TEMPLATES },
        upcomingTemplate: 'reminder',
        overdueTemplate: 'overdue',
        defaultIntervalDays: 7,
        countryCode: '91',
        contactPhone: '+91 94470 58210'
    }
})

export const createEmptyData = (): ClinicData => ({
    version: 1,
    patients: [],
    visits: [],
    prescriptions: [],
    followUps: [],
    reminders: [],
    treatments: createDefaultTreatments(),
    settings: createDefaultSettings(),
    counters: {
        patient: 1000,
        prescription: 1000
    },
    meta: {
        sample: false,
        sampleNoticeDismissed: false,
        createdAt: new Date(0).toISOString()
    }
})

export const MEDICINE_SUGGESTIONS = [
    'Amoxicillin 500 mg',
    'Amoxicillin + Clavulanic acid 625 mg',
    'Azithromycin 500 mg',
    'Clindamycin 300 mg',
    'Cefixime 200 mg',
    'Metronidazole 400 mg',
    'Ibuprofen 400 mg',
    'Ibuprofen 400 mg + Paracetamol 325 mg',
    'Aceclofenac 100 mg + Paracetamol 325 mg',
    'Diclofenac 50 mg',
    'Ketorolac 10 mg',
    'Paracetamol 650 mg',
    'Paracetamol syrup 250 mg/5 ml',
    'Pantoprazole 40 mg',
    'Chlorhexidine 0.2% mouthwash',
    'Povidone-iodine 2% gargle',
    'Benzydamine mouthwash',
    'Lidocaine 2% oral gel',
    'Metronidazole + Chlorhexidine gum gel',
    'Potassium nitrate desensitising toothpaste',
    'Triamcinolone 0.1% oral paste',
    'Vitamin B complex',
    'Serratiopeptidase 10 mg'
]

export const DOSAGE_SUGGESTIONS = ['1 tablet', '1 capsule', '1/2 tablet', '5 ml', '10 ml', 'Pea-sized amount', 'Apply a thin layer']

export const FREQUENCY_SUGGESTIONS = ['1-0-1', '1-1-1', '1-0-0', '0-0-1', '0-1-0', '1-1-1-1', 'SOS', 'Once daily', 'Twice daily', 'Three times daily']

export const DURATION_SUGGESTIONS = ['1 day', '3 days', '5 days', '7 days', '10 days', '14 days', '1 month']

export const MEDICINE_INSTRUCTION_SUGGESTIONS = ['After food', 'Before food', 'At bedtime', 'If pain', 'Rinse for 30 seconds', 'Apply on the gums', 'Do not swallow']
